import { useState, useEffect } from 'react';
import { useMediaQuery, useTheme, Theme, SvgIconProps } from '@mui/material';
import {
  CcfCustomWorkspaceIcon,
  CcfPopOver,
  PopOverData,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';

import {
  LocalStorageHelper,
  PersistentPanel,
  StorageKeys,
} from '@nice-ccf/core-sdk';
import { getCustomWorkspaces, globalActions } from '../../global.app.slice';
import customworkspaceStyles from '../ccf-custom-workspace.styles';

/**
 * Interface to declare all the CustomWorkspace menu item details
 */
interface CustoWorkspaceMenuItem {
  /**
   * @remarks - id of CustomWorkspace menu item
   */
  id: string;

  /**
   * @remarks - label of CustomWorkspace menu item
   */
  label: string;

  /**
   * @remarks - tooltip for CustomWorkspace menu item
   */
  toolTip: string;

  /**
   * @remarks - url of CustomWorkspace menu item
   */
  url: string;

  /**
   * @remarks - will popover menu close on selection of menu item
   */
  closeOnSelection: boolean;
}

/**
 * Used for SvgIcon styling and along with popover style handling
 * We will need this for upcoming funtionality development
 **/
export interface CcfCustomWorkspacePopoverProps extends SvgIconProps {
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'center' | 'left' | 'right';
  };
  iconWidth?: string;
  isContact?: boolean;
  labelComponent?: React.ReactNode;
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'center' | 'left' | 'right';
  };
}

export const defaultPopOverState: PopOverData = {
  popOverHeader: 'Custom Workspace',
  menuItems: [
    {
      items: [],
    },
  ],
};

/**
 * Used for creating a PopOver Dialogue for Launch indicator on Navigation bar and in contact card
 * @example `<CcfCustomWorkspacePopover />`
 * @example `<CcfCustomWorkspacePopover htmlColor={htmlColor} viewBox={viewBox} isContact />`
 */
export const CcfCustomWorkspacePopover = ({
  anchorOrigin,
  transformOrigin,
  htmlColor,
  viewBox,
  labelComponent,
}: CcfCustomWorkspacePopoverProps) => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const customWorkspaces = useSelector(getCustomWorkspaces);
  const [popOverMenuItems, setPopOverMenuItems] = useState(defaultPopOverState);
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));
  const styles = customworkspaceStyles(theme);

  /**
   * Function to handle the integration of redux data in UI component
   * Internal details - sets the Custom Workspace Labels
   * @param args - none,
   * @example setcustomworkspaceData()
   */
  const setcustomworkspaceData = () => {
    const cwData: CustoWorkspaceMenuItem[] = customWorkspaces.map((cw) => ({
      id: cw.persistentPanelId + '',
      label: cw.persistentPanelLabel,
      toolTip: cw.persistentPanelLabel,
      url: cw.persistentPanelURI,
      closeOnSelection: true,
    }));
    const customWorkspaceData = {
      popOverHeader: 'Custom Workspace',
      menuItems: [
        {
          items: [...cwData],
        },
      ],
    };
    setPopOverMenuItems(customWorkspaceData);
  };

  useEffect(() => {
    setcustomworkspaceData();
  }, [customWorkspaces]);

  /**
   * Function to handle pop over item selection
   * @param item -PopOverMenuItem
   * @param event -React.MouseEvent<HTMLElement>
   * @example - onPopOverItemSelection(item, e)
   */
  const onPopOverItemSelection =
    (item: CustoWorkspaceMenuItem, event?: React.MouseEvent<HTMLElement>) =>
    () => {
      const activeItem: PersistentPanel = {
        persistentPanelId: +item.id,
        persistentPanelLabel: item.label,
        persistentPanelURI: item.url,
      };
      LocalStorageHelper.setItem(
        StorageKeys.ACTIVE_CUSTOMWORKSPACE,
        activeItem
      );
      dispatch(globalActions.setActivePersistentPanel(activeItem));
    };

  return (
    <CcfPopOver
      customId="agent_custom_workspace_list"
      optionList={popOverMenuItems}
      onPopOverItemSelection={onPopOverItemSelection}
      iconComponent={
        <CcfCustomWorkspaceIcon
          htmlColor={htmlColor}
          viewBox={viewBox}
          //sx={styles.launchIcon}
        />
      }
      labelComponent={labelComponent}
      anchorOrigin={
        anchorOrigin ||
        (isSmView
          ? { vertical: 'bottom', horizontal: 'right' }
          : { vertical: 'top', horizontal: 'right' })
      }
      style={isSmView ? { top: '-2rem' } : { marginLeft: '3rem' }}
      tooltip
      transformOrigin={transformOrigin}
    />
  );
};
